import { useState } from 'react';

import { handleApiError } from 'api/error-handling';
import useAssignGate from 'hooks/workout/useAssignGate';
import useDeleteWorkout from 'hooks/workout/useDeleteWorkout';
import useToggleWorkoutVisibility from 'hooks/workout/useToggleWorkoutVisibility';
import { useNavigate } from 'react-router-dom';
import notificationHandler from 'utils/notifications';

interface UseWorkoutAssignmentArgs {
  kiloClassTypeId: string;
  kiloClassTypeName: string;
  date: string;
  workoutId?: string | null;
  workoutTemplateId?: string | null;
  isVisibleToAthletes?: boolean;
  view?: 'week' | 'agenda';
}

export interface UseWorkoutAssignmentResult {
  canMutate: boolean;
  isAssignBlocked: boolean;
  assignBlockedLabel: string;
  assignModalOpened: boolean;
  deleteModalOpened: boolean;
  deleteLoading: boolean;
  openAssignModal: () => void;
  closeAssignModal: () => void;
  closeDeleteModal: () => void;
  viewWhiteboard: () => void;
  toggleVisibility: () => void;
  editWorkout: () => void;
  changeWorkout: () => void;
  requestDelete: () => void;
  confirmDelete: () => void;
}

export default function useWorkoutAssignment({
  kiloClassTypeId,
  kiloClassTypeName,
  date,
  workoutId,
  workoutTemplateId,
  isVisibleToAthletes = true,
  view,
}: UseWorkoutAssignmentArgs): UseWorkoutAssignmentResult {
  const navigate = useNavigate();
  const { canMutate } = useAssignGate();
  const { successNotification, errorNotification } = notificationHandler();
  const [assignModalOpened, setAssignModalOpened] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const deleteMutation = useDeleteWorkout();
  const visibilityMutation = useToggleWorkoutVisibility();

  const isAssignBlocked = !canMutate;
  const assignBlockedLabel = 'You do not have permission to assign workouts';

  const viewWhiteboard = (): void => {
    navigate('/workouts/whiteboard', { state: { kiloClassTypeId, date } });
  };

  const toggleVisibility = (): void => {
    if (!workoutId) return;
    const next = !isVisibleToAthletes;

    visibilityMutation.mutate(
      { workoutId, isVisibleToAthletes: next },
      {
        onSuccess: () => {
          successNotification(
            next ? 'Workout shown' : 'Workout hidden',
            next
              ? 'Athletes booked into this class can see it again.'
              : 'Athletes booked into this class can no longer see it.',
          );
        },
        onError: error => {
          errorNotification('Update failed', handleApiError(error).message);
        },
      },
    );
  };

  const editWorkout = (): void => {
    if (workoutId) {
      navigate(`/workouts/programming/edit/${workoutId}`, { state: { date, kiloClassTypeName, view } });
    } else if (workoutTemplateId) {
      navigate(`/workouts/library/templates/edit/${workoutTemplateId}`);
    }
  };

  const changeWorkout = (): void => setAssignModalOpened(true);
  const requestDelete = (): void => setDeleteModalOpened(true);

  const confirmDelete = (): void => {
    if (!workoutId) return;
    deleteMutation.mutate(workoutId, {
      onSuccess: () => {
        successNotification('Workout deleted', `${kiloClassTypeName} no longer has a workout assigned.`);
        setDeleteModalOpened(false);
      },
      onError: error => {
        errorNotification('Delete failed', handleApiError(error).message);
      },
    });
  };

  return {
    canMutate,
    isAssignBlocked,
    assignBlockedLabel,
    assignModalOpened,
    deleteModalOpened,
    deleteLoading: deleteMutation.isLoading,
    openAssignModal: () => setAssignModalOpened(true),
    closeAssignModal: () => setAssignModalOpened(false),
    closeDeleteModal: () => setDeleteModalOpened(false),
    viewWhiteboard,
    toggleVisibility,
    editWorkout,
    changeWorkout,
    requestDelete,
    confirmDelete,
  };
}
